import { Marquee } from '../Marquee'
import { ComponentProps, memo, useEffect, useMemo } from 'react'
import { Message } from '../Message'
import { withDateSystemMessages } from '../../utils'
import { useTranslations } from '../../contexts'
import { TypingIndicator } from '../TypingIndicator'
import { webchatClasses } from '../../styles/classes'
import clsx from 'clsx'
import type { Feedback, IntegrationMessage, Renderers, RichBlockMessage } from '../../types'
import { nanoid } from 'nanoid'
import { useStickToBottom } from '../../hooks/useStickToBottom'

type MessageListProps = {
  messages?: RichBlockMessage[]
  isTyping?: boolean
  headerMessage?: string
  renderers?: Partial<Renderers>
  botAvatar?: string
  botDescription?: string
  botName?: string
  sendMessage?: (payload: IntegrationMessage['payload']) => void
  addMessageFeedback?: (messageId: string, feedback: Feedback) => void
  showMarquee?: boolean
  showDateSystemMessage?: boolean
  showMessageStatus?: boolean
  showIncomingMessageAvatar?: boolean
  showOutgoingMessageAvatar?: boolean
}

const scrollContainerStyle: React.CSSProperties = {
  height: '100%',
  width: '100%',
  overflowY: 'auto',
  scrollbarWidth: 'none',
  overscrollBehaviorY: 'contain',
}

export const MessageList = memo(
  ({
    className,
    messages = [],
    isTyping,
    headerMessage,
    renderers,
    botAvatar,
    botDescription,
    botName,
    sendMessage,
    addMessageFeedback,
    showMarquee = true,
    showDateSystemMessage = true,
    showMessageStatus = true,
    showIncomingMessageAvatar = true,
    showOutgoingMessageAvatar = false,
    ...props
  }: ComponentProps<'div'> & MessageListProps) => {
    const { messageList } = webchatClasses
    const translations = useTranslations()
    const messagesWithDates = useMemo(() => {
      if (showDateSystemMessage) return withDateSystemMessages(messages, translations)
      return messages
    }, [messages, translations])

    const lastDeliveredMessageId = useMemo(() => {
      const outgoingMessages = messagesWithDates.filter((m) => m.direction === 'outgoing' && m.status !== 'pending')
      return outgoingMessages[outgoingMessages.length - 1]?.id
    }, [messagesWithDates])

    const lastMessage = messagesWithDates[messagesWithDates.length - 1]
    const lastMessageId = lastMessage?.id ?? ''
    const contentKey = `${messagesWithDates.length}:${lastMessageId}:${isTyping}`
    const { scrollRef, isAtBottom, scrollToBottom } = useStickToBottom(contentKey)

    // Scroll to bottom when user sends a message, even if scrolled up
    useEffect(() => {
      if (lastMessage?.direction === 'outgoing') {
        scrollToBottom()
      }
    }, [lastMessageId])

    const shouldShowAvatar = (message: RichBlockMessage) => {
      if (message.direction === 'incoming') {
        return showIncomingMessageAvatar
      }

      if (message.direction === 'outgoing') {
        return showOutgoingMessageAvatar
      }
    }

    return (
      <div {...props} className={clsx(messageList.container.className, className)}>
        <div ref={scrollRef} style={scrollContainerStyle}>
          <div className={messageList.viewPort.className}>
            {headerMessage && <span {...messageList.headerMessage}>{headerMessage}</span>}
            {showMarquee && <Marquee {...{ botAvatar, botDescription, botName }} />}
            {messagesWithDates.map((message) => (
              <Message
                key={message.metadata?.clientMessageId || message.id}
                renderers={renderers}
                sendMessage={sendMessage}
                addMessageFeedback={addMessageFeedback}
                isLastDeliveredMessage={showMessageStatus && message.id === lastDeliveredMessageId}
                showAvatar={shouldShowAvatar(message)}
                {...message}
              />
            ))}
            {isTyping && (
              <Message
                id={nanoid()}
                renderers={renderers}
                sendMessage={sendMessage}
                direction={'incoming'}
                timestamp={new Date()}
                showAvatar={showIncomingMessageAvatar}
                // TODO: Fix this hack
                block={undefined as any}
                sender={
                  messages.filter((msg) => msg.direction === 'incoming').at(-1)?.sender ?? {
                    name: botName || 'Bot',
                    avatar: botAvatar,
                  }
                }
              >
                <TypingIndicator />
              </Message>
            )}
          </div>
        </div>
        {!isAtBottom && <button className={messageList.scrollDownButton.className} onClick={() => scrollToBottom()} />}
      </div>
    )
  }
)

MessageList.displayName = 'MessageList'
